import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Store } from '@ngrx/store';
import { find, lowerCase, map, set, upperFirst } from 'lodash';
import * as moment from 'moment';
import { UtilitiesService } from 'Src/ng2/shared/services/utilities/utilities.service';
import { IStudent } from '../../../shared/typings/interfaces/student.interface';
import { IUser } from '../../../shared/typings/interfaces/user.interface';
import { StudentPanelService } from '../student-panel.service';
import { ImUser } from './../../../shared/services/im-models/im-user';
import BasicInfoConfig from './student-basic-info-panel.config';
import { TNvTooltipData } from 'Src/ng2/shared/nv-tooltip/nv-tooltip.interface';
import { ToggleService } from 'Src/ng2/shared/services/toggle/toggle.service';
import { Toggles } from 'Src/ng2/shared/constants/toggles.constant';

type TAcademicOption = {
  name: string;
  _id: string;
};

type TEopFinancialEligibilityOption = {
  name: string;
  value: string;
};

type TSubtext = {
  text: string;
  url: string;
};

type tableRow = {
  field: string;
  value: string | number;
  fieldTooltip?: TNvTooltipData;
  valueTooltip?: TNvTooltipData;
  highlight?: string;
  editable?: boolean;
  options?: TAcademicOption[] | TEopFinancialEligibilityOption[];
  display?: boolean;
  subtext?: TSubtext;
};

type IUserOption = {
  firstName: string;
  lastName: string;
  name: string;
  _id: string;
  email: string;
};

@Component({
  selector: 'student-basic-info-panel',
  template: require('./student-basic-info-panel.component.html'),
  styles: [require('./student-basic-info-panel.scss')],
  encapsulation: ViewEncapsulation.None,
})
export class StudentBasicInfoPanel implements OnInit, OnChanges {
  @Input() student: IStudent;
  @Input() currentUser: IUser;
  @Input() schoolUsers: IUser[];
  @Input() collapsed: boolean;
  @Input() isSummerSchoolStudent: boolean;
  @Output() saveStudent: EventEmitter<any> = new EventEmitter();
  public panelTitle: string;

  // local
  public table1: tableRow[];
  private table1Data;
  public table2: tableRow[];
  private table2Data;
  private tableColumns;
  private academicOptions: TAcademicOption[];
  private userOptions: IUserOption[];
  public risksTable;
  public deviceAccessTable: tableRow[];
  private eopFinancialEligibilityOptions: TEopFinancialEligibilityOption[];
  public isEditingUser: boolean;

  constructor(
    private studentPanel: StudentPanelService,
    private store: Store<any>,
    private imUser: ImUser,
    private utilitiesService: UtilitiesService,
    private toggleService: ToggleService,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    this.setUI();
  }

  ngOnInit() {
    this.setUI();
  }

  setUI(): void {
    this.panelTitle = BasicInfoConfig.title;
    this.isEditingUser = this.imUser.isEditingUser(this.currentUser);
    this.collapsed = this.studentPanel.getInitialPanelState('student-basic-info-panel');

    const {
      studentDetails,
      schoolStatus,
      ellDetails,
      spedDetails,
      assmnts,
      attendanceDetails,
      schoolRiskGroupELA,
      schoolRiskGroupMath,
      att,
      isMS,
      isES,
      isHS,
      eopFinancialEligibility,
      atsContactDetails,
      schoolId,
    } = this.student;

    const admitDateValid = moment(studentDetails.admitDate).isValid();
    const admitDate = admitDateValid ? moment(studentDetails.admitDate).format('MMM D, YYYY') : '&mdash;';

    const birthDateValid = moment(studentDetails.dob).isValid();
    const birthDate = birthDateValid ? moment(studentDetails.dob).format('MMM D, YYYY') : '&mdash;';

    const homePhone =
      atsContactDetails && atsContactDetails.home && atsContactDetails.home.phone
        ? atsContactDetails.home.phone
        : '&mdash;';

    const minutes = attendanceDetails.transit.timeMinutes;
    const transitTime = minutes ? `${minutes} minutes` : `&mdash;`;
    const isEmsStudent = isES === true || isMS === true;
    const isHsStudent = isHS === true;

    const tempHousingStatus = this.utilitiesService.getBooleanOrDash(studentDetails.tempResFlag);
    const housingStatus = studentDetails.housingStatus || '&mdash;';

    const learningPreference = studentDetails.learningPreference || '&mdash;';

    this.table1 = [
      { field: 'Class', value: studentDetails.classOf || `&mdash;`, display: isHsStudent },
      { field: 'Grade', value: studentDetails.currGradeLevel || `&mdash;`, display: true },
      { field: 'Status', value: schoolStatus || `&mdash;`, display: true },
      { field: 'Official class', value: studentDetails.officialClass || `&mdash;`, display: true },
      { field: 'Admit Date', value: admitDate, display: true },
      { field: 'Birth Date', value: birthDate, display: true },
      { field: 'Home phone', value: homePhone, display: true },
      { field: 'Home language', value: studentDetails.homeLanguage || `&mdash;`, display: true },
      { field: 'Transit time', value: transitTime, display: true },
      { field: 'Temporary housing status', value: tempHousingStatus, display: true },
      { field: 'Housing status', value: housingStatus || `No`, display: true },
      { field: 'Learning preference', value: learningPreference, display: true },
      { field: 'Home School DBN', value: schoolId, display: this.isSummerSchoolStudent },
    ];

    let isEll = this.utilitiesService.getBooleanOrDash(ellDetails.isEll);

    let hasIEP = this.utilitiesService.getBooleanOrDash(spedDetails.isSped);
    const atsCounselor = studentDetails.counselor || `&mdash;`;

    let academicGroupELA;
    if (schoolRiskGroupELA) academicGroupELA = schoolRiskGroupELA;
    else academicGroupELA = '—';

    let academicGroupMath;
    if (schoolRiskGroupMath) academicGroupMath = schoolRiskGroupMath;
    else academicGroupMath = '—';

    this.academicOptions = [
      { name: '—', _id: null },
      { name: 'Pushable 2', _id: 'Pushable 2' },
      { name: 'Pushable 3', _id: 'Pushable 3' },
      { name: 'Slippable 2', _id: 'Slippable 2' },
      { name: 'Slippable 3', _id: 'Slippable 3' },
    ];

    this.userOptions = map(this.schoolUsers, (userRecord: IUser) => {
      const { preferredName, name } = userRecord;
      const firstName = preferredName.firstName ? preferredName.firstName : name.firstName;

      const lastName = preferredName.lastName ? preferredName.lastName : name.lastName;

      const fullName = `${firstName} ${lastName}`;
      const email = this.imUser.getEffectiveEmail(userRecord);
      return { name: fullName, _id: userRecord._id, email, firstName, lastName };
    });

    this.eopFinancialEligibilityOptions = [
      { name: '—', value: null },
      { name: 'Eligible', value: 'Eligible' },
      { name: 'Not eligible', value: 'Not eligible' },
    ];

    const { summary } = assmnts;
    const doeRiskGroupEla = find(summary, assmnt => {
      return assmnt.name === 'State Assessment' && assmnt.subj === 'ELA';
    });
    const doeRiskGroupMath = find(summary, assmnt => {
      return assmnt.name === 'State Assessment' && assmnt.subj === 'Math';
    });

    // todo: fix this
    /* istanbul ignore next */
    const doeRiskGroupAttendance = att.riskGroup ? att.riskGroup : '—';

    const userCanEdit = this.imUser.isEditingUser(this.currentUser);

    let maxYear: string = '0'; // can't be null since a string is never greater than null
    const recentNYSESLATPL: string = ellDetails.nyseslat.examScoreHistory.reduce((acc, { nyseslat, year }) => {
      if (nyseslat && year > maxYear) {
        acc = `${nyseslat} (${year})`;
        maxYear = year;
      }
      return acc;
    }, '&mdash;');

    this.table2 = this.hideEditableData([
      { field: 'ATS Counselor', value: atsCounselor, editable: false, display: true },
      { field: 'SPED', value: hasIEP, editable: false, display: true },
      { field: 'ELL', value: isEll, editable: false, display: true },
      { field: 'LEP status', value: ellDetails.lepStatus || `&mdash;`, display: true },
      { field: 'Most Recent NYSESLAT PL', value: recentNYSESLATPL, display: true },
      {
        field: 'EOP financial eligibility',
        value: eopFinancialEligibility || '—',
        editable: true,
        options: this.eopFinancialEligibilityOptions,
        display: isHsStudent,
        subtext: { text: 'See eligibility guidelines', url: 'https://www.suny.edu/attend/academics/eop/' },
      },
    ]);

    this.risksTable = this.hideEditableData([
      {
        field: 'ELA (DOE)',
        value: doeRiskGroupEla ? doeRiskGroupEla.doeRiskGroup : '—',
        display: isEmsStudent,
      },
      {
        field: 'ELA (School)',
        value: this.truncate(academicGroupELA),
        editable: userCanEdit,
        options: this.academicOptions,
        display: isEmsStudent,
      },
      {
        field: 'Math (DOE)',
        value: doeRiskGroupMath ? doeRiskGroupMath.doeRiskGroup : '—',
        display: isEmsStudent,
      },
      {
        field: 'Math (School)',
        value: this.truncate(academicGroupMath),
        editable: userCanEdit,
        options: this.academicOptions,
        display: isEmsStudent,
      },
      {
        field: 'Attendance (DOE)',
        value: doeRiskGroupAttendance,
        display: true,
      },
    ]);

    this.setDeviceAccessTable();

    this.tableColumns = ['field', 'value'];

    this.table1Data = new MatTableDataSource(this.table1);
    this.table2Data = new MatTableDataSource(this.table2);
  }

  private hideEditableData(table: tableRow[]): tableRow[] {
    // If this student attends this school, show table as normal
    if (!this.isSummerSchoolStudent) return table;

    // Otherwise, hide editable data
    return table.map(row => {
      const newRow = { ...row };
      if (newRow.editable) {
        newRow.value = '—';
        newRow.editable = false;
      }
      return newRow;
    });
  }

  changeSchoolRiskGroupEla(group): void {
    const path = 'schoolRiskGroupELA';
    const patch = set({}, path, group);
    this.saveStudent.emit({ patch, path });
  }

  changeSchoolRiskGroupMath(group): void {
    const path = 'schoolRiskGroupMath';
    const patch = set({}, path, group);
    this.saveStudent.emit({ patch, path });
  }

  changeEOPEligibility(option): void {
    const path = 'eopFinancialEligibility';
    const patch = set({}, path, option);
    this.saveStudent.emit({ patch, path });
  }

  onChangeHandler(field: string, option): void {
    switch (field) {
      case 'ELA (School)':
        this.changeSchoolRiskGroupEla(option._id);
        this.risksTable[1].value = this.truncate(option.name);
        break;
      case 'Math (School)':
        this.changeSchoolRiskGroupMath(option._id);
        this.risksTable[3].value = this.truncate(option.name);
        break;
      case 'EOP financial eligibility':
        this.changeEOPEligibility(option.value);
        this.table2[4].value = this.truncate(option.name);
        break;
      default:
        break;
    }
  }

  truncate(text): string {
    return text.length > 12 ? `${text.substring(0, 12)}...` : text;
  }

  isOptionActive(field, option, table) {
    let row = find(table, (tableRow: tableRow) => {
      return tableRow.field === field;
    });
    return this.truncate(row.value) === this.truncate(option);
  }

  togglePanelBody(): void {
    this.collapsed = this.studentPanel.togglePanelBody(this.collapsed, this.panelTitle);
  }

  setDeviceAccessTable(): void {
    const { deviceAccess } = this.student;
    if (deviceAccess) {
      const shippedDate = deviceAccess && deviceAccess.shippedDate;
      const valueTooltip = shippedDate
        ? {
            tableContent: {
              headers: [],
              rowData: [['Shipped date', moment(shippedDate).format('MMM D, YYYY')]],
            },
          }
        : null;

      this.deviceAccessTable = [
        {
          field: 'Internet access',
          value: this.utilitiesService.getBooleanOrDash(deviceAccess.hasInternetAccess),
          display: true,
          fieldTooltip: 'From Remote Learning Device Request form administered by DOE',
        },
        {
          field: 'Computer access',
          value: this.utilitiesService.getBooleanOrDash(deviceAccess.hasComputerAccess),
          display: true,
          fieldTooltip: 'From Remote Learning Device Request form administered by DOE',
        },
        {
          field: 'Smartphone access',
          value: this.utilitiesService.getBooleanOrDash(deviceAccess.hasSmartPhoneAccess),
          display: true,
          fieldTooltip: 'From Remote Learning Device Request form administered by DOE',
        },
        {
          field: 'Device request status',
          value: deviceAccess.requestStatus ? upperFirst(lowerCase(deviceAccess.requestStatus)) : '—',
          display: true,
          valueTooltip,
        },
      ];
    } else {
      const isGoogleClassroomToggleOn = this.toggleService.getToggleState(Toggles.TOGGLE_GOOGLE_CLASSROOM);
      this.deviceAccessTable = isGoogleClassroomToggleOn
        ? [
            {
              field: 'Internet access',
              value: '—',
              display: true,
              fieldTooltip: 'From Remote Learning Device Request form administered by DOE',
            },
            {
              field: 'Computer access',
              value: '—',
              display: true,
              fieldTooltip: 'From Remote Learning Device Request form administered by DOE',
            },
            {
              field: 'Smartphone access',
              value: '—',
              display: true,
              fieldTooltip: 'From Remote Learning Device Request form administered by DOE',
            },
            {
              field: 'Device request status',
              value: '—',
              display: true,
              fieldTooltip: 'From Remote Learning Device Request form administered by DOE',
            },
          ]
        : [];
    }
  }
}
